import {useState} from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement,
} from "@stripe/react-stripe-js";
import {Button} from "@chakra-ui/react";
import {paymentMethodsApi} from "../../Account/Billing/api";

// A SetupIntent can only be confirmed while it is in
// requires_payment_method / requires_confirmation / requires_action. Once it
// succeeds or is cancelled, confirming it again returns
// `setup_intent_unexpected_state` and the only way forward is a new intent.
const REUSABLE_ERROR_TYPES = new Set(["validation_error", "card_error"]);

function isSetupIntentUnusable(error) {
  if (!error) return false;
  if (error.code === "setup_intent_unexpected_state") return true;
  const status = error.setup_intent?.status;
  if (status === "succeeded" || status === "canceled") return true;
  return !REUSABLE_ERROR_TYPES.has(error.type);
}

export default function StripeAddCCForm({
  setErrorMessage,
  setSuccessMessage,
  onSaved,
  onConfirmFailed,
  dashState,
  patient,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  // Gate the submit on Stripe's own ready callbacks instead of on
  // `elements.getElement(...)` during render: the element is created in the
  // child's layout effect, which does not re-render this component, so a
  // render-time check can leave the form permanently without a Save button.
  const [paymentReady, setPaymentReady] = useState(false);
  const [addressReady, setAddressReady] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    let confirmError = null;
    let savedPaymentMethodId = null;
    let confirmedSetupIntentId = null;
    try {
      const {error, setupIntent} = await stripe.confirmSetup({
        elements,
        confirmParams: {return_url: window.location.href},
        redirect: "if_required",
      });
      confirmError = error || null;
      confirmedSetupIntentId = setupIntent?.id || null;
      savedPaymentMethodId =
        typeof setupIntent?.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id || null;
    } catch (error) {
      confirmError = error;
    }

    if (confirmError) {
      setErrorMessage(confirmError.message || "Could not save card.");
      setIsLoading(false);
      // Only replace the SetupIntent when it can no longer be confirmed —
      // a consumed one answers `setup_intent_unexpected_state` on every
      // retry. `validation_error` and `card_error` leave the intent
      // usable, and replacing it there would remount <Elements> and wipe
      // everything the patient typed over a fixable field.
      if (isSetupIntentUnusable(confirmError)) onConfirmFailed?.();
      return;
    }

    // The card is saved at this point — Stripe attached it to the customer
    // because the SetupIntent was created with `customer` server-side.
    // NOTHING below may turn a successful save into a failure.
    //
    // The clinic is told by the server: this names the confirmed SetupIntent
    // and the server checks with Stripe that it is this patient's before
    // writing the notice. The portal used to write the notice itself, with
    // whatever text it chose.
    try {
      if (confirmedSetupIntentId) {
        await paymentMethodsApi.reportAdded({jwt: dashState?.jwt || null}, confirmedSetupIntentId);
      }
    } catch (error) {
      // Best effort: the staff notice is not worth losing a saved card over.
      console.error("Card saved, but the clinic could not be told:", error);
    }

    setIsLoading(false);
    setSuccessMessage("Successfully added new credit card.");
    onSaved?.(savedPaymentMethodId);
  };

  return (
    <>
      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement
          options={{terms: {card: "never"}}}
          onReady={() => setPaymentReady(true)}
        />
        {paymentReady && (
          <AddressElement
            options={{mode: "billing", autocomplete: {mode: "automatic"}}}
            onReady={() => setAddressReady(true)}
          />
        )}
        {paymentReady && (
          <Button
            isLoading={isLoading}
            my="5"
            w="100%"
            bg="gray.900"
            color="white"
            type="submit"
            isDisabled={
              isLoading || !stripe || !elements || !paymentReady || !addressReady
            }
            px="10"
            _hover={{bg: "gray.800"}}
            _active={{bg: "gray.700"}}
            transition="all 0.15s ease"
          >
            Save
          </Button>
        )}
      </form>
    </>
  );
}
